import { useMemo } from 'react'
import {
  PresentationManifest,
  isDefinedPresentationManifestFrame,
} from '@sketch-hq/sketch-web-renderer'
import config from '@sketch/env-config'

type URLMap = Record<string, string>
type ImageTokens = Record<string, string>

type Result = {
  imagesURLMap: URLMap
  fragmentsURLMap: URLMap
}

/**
 * Build image and fragment URL maps so the renderer can map image and fragment
 * ids to their real valid urls.
 */
export function useURLMaps(
  manifest: PresentationManifest | null,
  imageTokens: ImageTokens | null
): Result | null {
  return useMemo(() => {
    if (manifest === null || imageTokens === null) return null

    const urlMaps: Result = {
      imagesURLMap: {},
      fragmentsURLMap: {},
    }

    for (const page of manifest.contents.pages) {
      const artboards = page.artboards || []
      const frames = page.frames || []

      /**
       * We currently have both artboard and frames in the manifest file (duplicated information)
       * Given they are added to the fragmentURLMap by "fragId" the artboard one should be overridden by the frame data (if existent)
       */
      const framesAndArtboards = [
        ...artboards.filter(isDefinedPresentationManifestFrame),
        ...frames.filter(isDefinedPresentationManifestFrame),
      ]

      for (const frame of framesAndArtboards) {
        const fragId = frame.fragment.path.split('.').shift()

        if (fragId) {
          urlMaps.fragmentsURLMap[fragId] = `${
            config.web_renderer_image_domain
          }/presentation/p/${page.id}/f/${fragId}?token=${imageTokens[page.id]}`
        }

        const images = frame.assets ?? []

        for (const image of images) {
          const imageId = image.path.split('.').shift()
          const token = imageTokens[page.id]
          // The same assets is sometimes used across multiple pages but if the user
          // doesn't have access to a page, we won't have a token for it.
          // Since we overwrite the images urls for the same assets across pages, we only
          // set it when we are sure we have token for it.
          // See https://linear.app/sketch/issue/SWEB-2409/default-image-fills-in-symbol-instances-do-not-render-in-preview
          if (imageId && token) {
            urlMaps.imagesURLMap[imageId] = `${
              config.web_renderer_image_domain
            }/presentation/p/${page.id}/i/${imageId}?token=${token}&format=:format`
          }
        }
      }
    }

    return urlMaps
  }, [manifest, imageTokens])
}
